import {
  CheckoutData,
  SuccesfullPartialPayment,
  TableStatus,
  Payment,
  PrintDoc,
  PrintJob,
  OrderItem,
  PartialItemsQuantityMap,
  PrinterPayemnt,
  TableCheckoutItem,
  PaymentDetails,
} from "@lemmonhq/types";
import { DateRange } from "@app/vue/components/index";
import { ordersAPI } from "./orders";
import { tablesAPI } from "./tables";
import { getAPIClient, routingFetch } from "../helpers";
import { shiftsAPI } from "./shifts";

export type PaymentsListResponse = {
  data: Payment[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
};
export const paymentsAPI = {
  createCreditNode: async (attrs: {
    amount: number;
    notes?: string;
    payment_method: string;
    reference_payment_id: number;
  }): Promise<void> => {
    const hc = getAPIClient();
    const activeShift = await shiftsAPI.getActiveShift();
    if (!activeShift) {
      throw new Error("no shift running");
    }
    const req = await hc.api.v2.payments.$post({
      json: {
        amount: attrs.amount,
        notes: attrs.notes,
        payment_method: attrs.payment_method,
        reference_payment_id: attrs.reference_payment_id,
        shift_id: activeShift.id,
      },
    });
    if (req.ok) {
      const resp = await req.json();
      console.log("resp", resp);
    } else {
      throw new Error("failed creating payment");
    }
  },
  updatePayment: async (
    paymentGuid: string,
    attrs: {
      payment_method: string;
    },
  ): Promise<void> => {
    const hc = getAPIClient();
    const req = await hc.api.v2.payments[":id"].$put({
      json: {
        payment_method: attrs.payment_method,
      },
      param: { id: paymentGuid },
    });
    if (req.ok) {
      const resp = await req.json();
      console.log("resp", resp);
    } else {
      throw new Error("failed creating payment");
    }
  },
  getPayment: async (id: number) => {
    const hc = getAPIClient();
    const req = await hc.api.payments[":id"].$get({
      param: { id: id.toString() },
    });
    if (req.ok) {
      const json = await req.json();
      return json as PaymentDetails;
    } else {
      throw new Error("unable to fetch payment");
    }
  },
  getPayments: async (
    range: DateRange,
    page: number,
    limit: number,
  ): Promise<PaymentsListResponse> => {
    try {
      const hc = getAPIClient();
      const param = {
        start_date: range.startDate,
        end_date: range.endDate,
        page,
        limit,
      };
      const req = await hc.api.v2.payments.$get({
        query: param,
      });
      console.log("req", req, param);
      if (!req.ok) {
        throw new Error("Failed to fetch payments");
      }
      const response = await req.json();
      return response;
    } catch (error) {
      console.error("Error fetching payments:", error);
      throw error;
    }
  },

  getPartialPayments: async (
    orderIds: number[],
  ): Promise<SuccesfullPartialPayment[]> => {
    const hc = getAPIClient();
    const response = await hc.api.partial_payments.$get({
      query: { orderIds: orderIds.join(",") },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch partial_payments");
    }
    return response.json();
  },

  getTablePayments: async (
    orderIds: number[],
  ): Promise<{
    partial_payments: SuccesfullPartialPayment[];
    payments: PaymentDetails[];
    payment_order_item_lines?: {
      id: number;
      restaurant_id: number;
      payment_id: number;
      order_id: number;
      order_item_id: number;
      quantity: number;
      unit_price: number;
      amount: number;
      line_type: string;
      created_at: string;
    }[];
    remaining_items?: SuccesfullPartialPayment[];
    integrity_warnings?: Record<string, any>[];
  }> => {
    const client = getAPIClient();
    try {
      const response = await client.api.v2.table_payments.$get({
        query: {
          orderIds: orderIds.join(","),
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch partial_payments");
      }
      // todo: debug why is this broken and needs a typecast
      return response.json() as any;
    } catch (error) {
      console.error("Error fetching partial_payments:", error);
      throw error;
    }
  },

  createSplitlPaymentForTable: async (
    payload: CheckoutData,
  ): Promise<{
    table: TableStatus;
    taxReceipt: PrintDoc;
    payment: PaymentDetails;
  }> => {
    console.log(
      "createPartialPaymentForTable",
      JSON.parse(JSON.stringify(payload)),
    );
    if (!payload.payload || !(payload.payload.type === "table")) {
      throw new Error("No table");
    }
    const response = await routingFetch(`/api/v2/table_payments/split_payment`, {
      method: "post",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (response.status == 422) {
        const msg = (await response.json()) as { message: string };
        throw new Error(msg.message);
      } else {
        throw new Error("Failed processing payment");
      }
    }
    const resp: any = await response.json();
    return resp;
  },
  createPartialPaymentForTable: async (
    payload: CheckoutData,
  ): Promise<{
    table: TableStatus;
    taxReceipt: PrintDoc;
    payment: PaymentDetails;
  }> => {
    console.log(
      "createPartialPaymentForTable",
      JSON.parse(JSON.stringify(payload)),
    );
    if (!payload.payload || !(payload.payload.type === "table")) {
      throw new Error("No table");
    }
    if (payload.payload.object.type !== "partial") {
      throw new Error("No items selected");
    }
    const hc = getAPIClient();
    const response = await hc.api.tables[":tableId"].partial_payment.$post({
      param: { tableId: payload.payload?.object.table_id.toString() },
      json: payload as any,
    });
    if (!response.ok) {
      if (response.status == 422) {
        const msg = (await response.json()) as unknown as { message: string };
        throw new Error(msg.message);
      } else {
        throw new Error("Failed processing payment");
      }
    }
    const resp: any = await response.json();
    return resp;
  },

  createPaymentForTable: async (
    payload: CheckoutData,
  ): Promise<{
    table: TableStatus;
    taxReceipt: PrintDoc;
    payment: PaymentDetails;
  }> => {
    console.log("createPaymentForTable", JSON.parse(JSON.stringify(payload)));
    if (!payload.payload || !(payload.payload.type === "table")) {
      throw new Error("No table");
    }
    if (payload.payload.object.type == "partial") {
      throw new Error("Please unselect partial payments");
    }
    const hc = getAPIClient();
    const response = await hc.api.tables[":tableId"].payment.$post({
      param: { tableId: payload.payload?.object.table_id.toString() },
      json: payload as any,
    });
    if (!response.ok) {
      if (response.status == 422) {
        const msg = (await response.json()) as unknown as { message: string };
        throw new Error(msg.message);
      } else {
        throw new Error("Failed processing payment");
      }
    }
    const resp: any = await response.json();
    return resp;
  },

  reduceOrderItemsQuantityFromTable: async (
    tableId: number,
    payload: { id: number; quantity: number }[],
    update_kitchen: boolean,
    shop: { print: (job: PrintJob) => Promise<void> },
  ): Promise<{ print_jobs: PrintJob[] }> => {
    console.log(
      "reduceOrderItemsQuantityFromTable",
      JSON.parse(JSON.stringify(payload)),
    );
    const hc = getAPIClient();
    const response = await hc.api.tables[":tableId"].items.$put({
      param: { tableId: tableId.toString() },
      json: { items: payload, update_kitchen } as any,
    });
    if (!response.ok) {
      throw new Error("Failed to reduceOrderItemsQuantityFromTable");
    }
    const resp: { print_jobs: PrintJob[] } = await response.json();

    for (const job of resp.print_jobs) {
      shop.print(job);
    }
    return resp;
  },

  getTaxReceipt: async (paymentId: number): Promise<PrintDoc> => {
    const hc = getAPIClient();
    const req = await hc.api.printjobs.receipt[":paymentId"].$get({
      param: { paymentId: paymentId.toString() },
    });
    if (!req.ok) {
      throw new Error("Failed to getTaxReceipt");
    }
    return await req.json();
  },
  getBill: async (
    tableId: number,
    attrs: {
      items?: TableCheckoutItem[];
      payment: PrinterPayemnt;
    },
  ): Promise<PrintDoc> => {
    let items = attrs.items;
    console.log("getBill items", items);
    let orderItems: OrderItem[] = [];
    let partialQuantityMap: PartialItemsQuantityMap | undefined;
    if (!items || items.length == 0) {
      const tables = await tablesAPI.getTablesMeta();
      const tableStatus = tables.find((el) => el.id === tableId);
      if (!tableStatus) {
        throw new Error("getBill: table not found " + tableId);
      }
      const orderIds = tableStatus.orders.map((el) => el.id);
      console.log("table status", tableStatus, orderIds);

      // Get table payments to exclude already paid items
      const tablePayments = await paymentsAPI.getTablePayments(orderIds);

      const orders = await ordersAPI.getOrders(orderIds);
      orderItems = orders
        .filter((el) => el.items)
        .map((el) => el.items || [])
        .flat();

      // Build map of paid quantities from partial payments
      const paidQuantityMap: Record<number, number> = {};
      tablePayments.partial_payments?.forEach((partialPayment) => {
        paidQuantityMap[partialPayment.order_item_id] =
          (paidQuantityMap[partialPayment.order_item_id] || 0) +
          partialPayment.quantity;
      });

      // Reduce quantities and filter out fully paid items
      orderItems = orderItems
        .map((item) => {
          const paidQty = paidQuantityMap[item.id] || 0;
          if (paidQty > 0) {
            return { ...item, quantity: item.quantity - paidQty };
          }
          return item;
        })
        .filter((item) => item.quantity > 0);
    } else {
      const orderIds = Array.from(new Set(items.map((el) => el.orderId)));
      const orderItemIds = new Set(items.map((el) => el.id));

      // Get table payments to exclude already paid items
      const tablePayments = await paymentsAPI.getTablePayments(orderIds);

      const orders = await ordersAPI.getOrders(orderIds);
      partialQuantityMap = items.reduce<PartialItemsQuantityMap>(
        (state, item) => {
          state[item.id.toString()] = item.quantity;
          return state;
        },
        {},
      );
      orderItems = orders
        .filter((el) => el.items)
        .map((el) => el.items || [])
        .flat()
        .filter((el) => orderItemIds.has(el.id));

      // Build map of paid quantities from partial payments
      const paidQuantityMap: Record<number, number> = {};
      tablePayments.partial_payments?.forEach((partialPayment) => {
        paidQuantityMap[partialPayment.order_item_id] =
          (paidQuantityMap[partialPayment.order_item_id] || 0) +
          partialPayment.quantity;
      });

      // Reduce quantities and filter out fully paid items
      orderItems = orderItems
        .map((item) => {
          const paidQty = paidQuantityMap[item.id] || 0;
          if (paidQty > 0) {
            return { ...item, quantity: item.quantity - paidQty };
          }
          return item;
        })
        .filter((item) => item.quantity > 0);
    }
    const payload: {
      orderItems: OrderItem[];
      partialQuantityMap?: PartialItemsQuantityMap;
      payment: PrinterPayemnt;
    } = {
      orderItems,
      payment: attrs.payment,
      partialQuantityMap,
    };
    const hc = getAPIClient();
    const req = await hc.api.printjobs.tables[":tableId"].bill.$post({
      param: { tableId: tableId.toString() },
      json: payload as any,
    });
    if (!req.ok) {
      throw new Error("Failed to getTaxReceipt");
    }
    return await req.json();
  },
};
